import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { AuthRedirectReason } from 'app/core/auth/auth-redirect-reason';
import { AuthSessionPolicy } from 'app/core/auth/models/auth-session-policy.model';
import { AuthSessionService } from 'app/core/auth/services/auth-session.service';
import { AuthTokenService } from 'app/core/auth/services/auth-token.service';
import { isExternalModuleRequest } from 'app/core/auth/utils/auth-http.utils';
import { KeycloakClientService } from 'app/core/auth/services/keycloak-client.service';
import { ConfigurationService } from 'app/core/config/configuration.service';
import { catchError, from, mergeMap, Observable, throwError } from 'rxjs';

const CONTENT_TYPE_KEY = 'Content-Type';
const ACCEPT_LANGUAGE_HEADER_KEY = 'Accept-Language';
const APPLICATION_JSON = 'application/json;charset=UTF-8';
const AUTH_RETRY_HEADER = 'X-Auth-Retry';

@Injectable()
export class AuthMasterInterceptor implements HttpInterceptor {
    constructor(
        private translocoService: TranslocoService,
        private keycloakClient: KeycloakClientService,
        private authToken: AuthTokenService,
        private authSession: AuthSessionService,
        private configurationService: ConfigurationService,
        private router: Router
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.keycloakClient.isLoggedIn()
            ? this.handleRequestWithCustomFields(req, next)
            : next.handle(req);
    }

    private handleRequestWithCustomFields(
        req: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        let headers = req.headers.set(ACCEPT_LANGUAGE_HEADER_KEY, this.translocoService.getActiveLang());

        if (!(req.body instanceof FormData)) {
            headers = headers.set(CONTENT_TYPE_KEY, APPLICATION_JSON);
        }

        const newReq = req.clone({ headers });

        return next.handle(newReq).pipe(
            catchError((error: HttpErrorResponse) => this.handleHttpError(error, newReq, next))
        );
    }

    private handleHttpError(
        error: HttpErrorResponse,
        req: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        if (!(error instanceof HttpErrorResponse)) {
            return throwError(() => error);
        }

        if (this.isExternalModuleRequest(req.url)) {
            return throwError(() => error);
        }

        if (error.status === 403) {
            this.router.navigate(['/forbidden']);
            return throwError(() => error);
        }

        if (error.status === 401 && !req.headers.has(AUTH_RETRY_HEADER)) {
            return from(this.authToken.updateToken()).pipe(
                mergeMap(() => {
                    const retryReq = req.clone({
                        setHeaders: { [AUTH_RETRY_HEADER]: 'true' },
                    });
                    return next.handle(retryReq);
                }),
                catchError(() => {
                    this.authSession.handleSessionEnded(
                        AuthRedirectReason.Http401AfterRetry,
                        AuthSessionPolicy.ReLogin,
                        { httpUrl: req.url }
                    );
                    return throwError(() => error);
                })
            );
        }

        return throwError(() => error);
    }

    private isExternalModuleRequest(url: string): boolean {
        try {
            const biAlertUrl = this.configurationService.getConfig().biAlertUrl;
            return isExternalModuleRequest(url, biAlertUrl);
        } catch {
            return false;
        }
    }
}
